import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import Layout from '@/components/Layout';
import { PayPalCheckout } from '@/components/PayPalCheckout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { useBookings } from '@/hooks/useBookings';
import { useTransferExtraItems } from '@/hooks/useTransferExtraItems';
import { supabase } from '@/integrations/supabase/client';
import { 
  User, 
  Phone, 
  Mail, 
  MapPin, 
  Users, 
  Car, 
  Crown, 
  Calendar,
  CreditCard,
  Plane,
  DollarSign,
  Banknote,
  Package,
  Baby,
  Luggage,
  PawPrint,
  Wifi,
  CupSoda
} from 'lucide-react';

interface BookingData {
  from: string;
  to: string;
  passengers: number;
  tripType: 'oneway' | 'roundtrip';
  serviceType: 'standard' | 'vip';
  price: number;
  route: any;
}

export const BookingDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { paymentMethods, loading: paymentLoading, getMethodTitle, getMethodDescription } = usePaymentMethods();
  const { createBooking } = useBookings();
  const { activeExtraItems } = useTransferExtraItems();
  const bookingData = location.state as BookingData;

  const [currentBooking, setCurrentBooking] = useState<any>(null);
  const [showPayPalCheckout, setShowPayPalCheckout] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState<string>('');
  const [paypalOptions, setPaypalOptions] = useState<any>(null);
  const [paypalConfigLoaded, setPaypalConfigLoaded] = useState(false);
  const [paypalConfigError, setPaypalConfigError] = useState<string | null>(null);

  const [selectedExtras, setSelectedExtras] = useState<Array<{
    extra_item_id: string;
    name: string;
    price: number;
    quantity: number;
  }>>([]);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    // Flight information
    arrivalDate: '',
    arrivalTime: '',
    airline: '',
    flightNumber: '',
    // Return flight information (for round trip)
    returnDate: '',
    returnTime: '',
    returnAirline: '',
    returnFlightNumber: '',
    // Extra information
    amountOfLuggage: '',
    remarks: '',
    // Payment method
    paymentMethod: '',
  });

  // Load PayPal configuration when PayPal is selected
  const loadPayPalConfig = async () => {
    try {
      setPaypalConfigError(null);
      console.log('Loading PayPal configuration...');
      
      const { data, error } = await supabase.functions.invoke('get-paypal-client-id');
      
      if (error) {
        throw new Error(`Failed to get PayPal config: ${error.message}`);
      }

      if (!data.success || !data.client_id) {
        throw new Error(data.error || 'PayPal not configured');
      }

      const options = {
        clientId: data.client_id,
        currency: "USD",
        intent: "capture" as const,
        "enable-funding": "card,venmo,paylater",
        "disable-funding": "",
        components: "buttons,marks",
        locale: "en_US"
      };

      console.log('PayPal configuration loaded successfully');
      setPaypalOptions(options);
      setPaypalConfigLoaded(true);
      return true;
    } catch (err: any) {
      console.error('Error loading PayPal config:', err);
      setPaypalConfigError(err.message || 'Failed to load PayPal configuration');
      return false;
    }
  };

  // Automatically load PayPal config when payment methods include PayPal
  useEffect(() => {
    const hasPayPal = paymentMethods.some(method => method.method_type === 'paypal');
    if (hasPayPal && !paypalOptions && !paypalConfigLoaded && !paypalConfigError) {
      console.log('Auto-loading PayPal configuration...');
      loadPayPalConfig();
    }
  }, [paymentMethods, paypalOptions, paypalConfigLoaded, paypalConfigError]);

  if (!bookingData) {
    navigate('/');
    return null;
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getIconComponent = (iconName: string) => {
    const iconMap: Record<string, any> = {
      Package,
      Baby,
      Luggage,
      PawPrint,
      Wifi,
      CupSoda,
    };
    const Icon = iconMap[iconName] || Package;
    return <Icon className="h-4 w-4" />;
  };

  const handleExtraToggle = (item: any, checked: boolean) => {
    if (checked) {
      setSelectedExtras([...selectedExtras, {
        extra_item_id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1
      }]);
    } else {
      setSelectedExtras(selectedExtras.filter(e => e.extra_item_id !== item.id));
    }
  };

  const handleExtraQuantityChange = (itemId: string, quantity: number) => {
    setSelectedExtras(selectedExtras.map(e => 
      e.extra_item_id === itemId ? { ...e, quantity } : e
    ));
  };

  const extrasCost = selectedExtras.reduce((sum, extra) => 
    sum + (extra.price * extra.quantity), 0
  );

  const totalWithExtras = (bookingData?.price || 0) + extrasCost;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.fullName || !formData.email || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required personal information fields.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.paymentMethod) {
      toast({
        title: "Payment Method Required",
        description: "Please select a payment method to proceed.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Prepare booking data
      const newBookingData = {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        from_location: bookingData?.from || '',
        to_location: bookingData?.to || '',
        passengers: bookingData?.passengers || 1,
        trip_type: bookingData?.tripType || 'oneway',
        service_type: bookingData?.serviceType || 'standard',
        total_price: totalWithExtras,
        arrival_date: formData.arrivalDate || null,
        arrival_time: formData.arrivalTime || null,
        airline: formData.airline || null,
        flight_number: formData.flightNumber || null,
        return_date: formData.returnDate || null,
        return_time: formData.returnTime || null,
        return_airline: formData.returnAirline || null,
        return_flight_number: formData.returnFlightNumber || null,
        amount_of_luggage: formData.amountOfLuggage || null,
        remarks: formData.remarks || null,
        extra_items: selectedExtras,
        extras_cost: extrasCost,
        payment_method: formData.paymentMethod as 'cash' | 'paypal' | 'stripe',
        payment_status: 'pending' as const,
        booking_status: 'pending' as const,
      };

      const newBooking = await createBooking(newBookingData);
      setCurrentBooking(newBooking);

      // Handle PayPal payment flow
      if (formData.paymentMethod === 'paypal') {
        // Show embedded PayPal checkout
        setShowPayPalCheckout(true);
        toast({
          title: "Booking Created",
          description: "Please complete your payment below.",
        });
      } else {
        // Navigate to confirmation page for other payment methods
        navigate('/booking-confirmation', { 
          state: { 
            booking: newBooking,
            bookingData: newBookingData 
          } 
        });
      }

    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: "Booking Error",
        description: "There was an error processing your booking. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Layout>
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="mb-10 text-center">
                <h1 className="text-4xl md:text-5xl font-bold mb-3 bg-gradient-to-r from-primary via-purple-600 to-primary bg-clip-text text-transparent">
                  Complete Your Booking
                </h1>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Please provide your details to complete the transfer booking
                </p>
              </div>

            {!showPayPalCheckout ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Booking Form */}
                <div className="lg:col-span-2">
                  <Card className="shadow-xl border-border/50 overflow-hidden">
                    <div className="bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/10 p-1">
                      <CardHeader className="bg-background">
                        <CardTitle className="flex items-center gap-2 text-2xl">
                          <User className="h-6 w-6 text-primary" />
                          Personal Information
                        </CardTitle>
                      </CardHeader>
                    </div>
                    <CardContent className="p-6">
                      <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Name Fields */}
                        <div className="space-y-2">
                          <Label htmlFor="fullName">Full Name *</Label>
                          <Input
                            id="fullName"
                            value={formData.fullName}
                            onChange={(e) => handleInputChange('fullName', e.target.value)}
                            required
                          />
                        </div>

                        {/* Contact Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              type="email"
                              value={formData.email}
                              onChange={(e) => handleInputChange('email', e.target.value)}
                              required
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone *</Label>
                            <Input
                              id="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={(e) => handleInputChange('phone', e.target.value)}
                              required
                            />
                          </div>
                        </div>

                        {/* Flight Information */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Plane className="h-5 w-5 text-primary" />
                            <h3 className="text-lg font-semibold">Share your flights info</h3>
                          </div>
                          
                          {/* Arrival Flight Information */}
                          <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                            <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Arrival Flight</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="arrivalDate">Arrival Date *</Label>
                                <Input
                                  id="arrivalDate"
                                  type="date"
                                  value={formData.arrivalDate}
                                  onChange={(e) => handleInputChange('arrivalDate', e.target.value)}
                                  min={new Date().toISOString().split('T')[0]}
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="arrivalTime">Arrival Time *</Label>
                                <Input
                                  id="arrivalTime"
                                  type="time"
                                  value={formData.arrivalTime}
                                  onChange={(e) => handleInputChange('arrivalTime', e.target.value)}
                                  required
                                />
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="airline">Airline *</Label>
                                <Input
                                  id="airline"
                                  value={formData.airline}
                                  onChange={(e) => handleInputChange('airline', e.target.value)}
                                  placeholder="e.g., American Airlines"
                                  required
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="flightNumber">Flight Number *</Label>
                                <Input
                                  id="flightNumber"
                                  value={formData.flightNumber}
                                  onChange={(e) => handleInputChange('flightNumber', e.target.value)}
                                  placeholder="e.g., AA1234"
                                  required
                                />
                              </div>
                            </div>
                          </div>

                          {/* Return Flight Information - Only for Round Trip */}
                          {bookingData.tripType === 'roundtrip' && (
                            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">Return Flight</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="returnDate">Return Date *</Label>
                                  <Input
                                    id="returnDate"
                                    type="date"
                                    value={formData.returnDate}
                                    onChange={(e) => handleInputChange('returnDate', e.target.value)}
                                    min={formData.arrivalDate || new Date().toISOString().split('T')[0]}
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="returnTime">Return Time *</Label>
                                  <Input
                                    id="returnTime"
                                    type="time"
                                    value={formData.returnTime}
                                    onChange={(e) => handleInputChange('returnTime', e.target.value)}
                                    required
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="returnAirline">Return Airline *</Label>
                                  <Input
                                    id="returnAirline"
                                    value={formData.returnAirline}
                                    onChange={(e) => handleInputChange('returnAirline', e.target.value)}
                                    placeholder="e.g., American Airlines"
                                    required
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="returnFlightNumber">Return Flight Number *</Label>
                                  <Input
                                    id="returnFlightNumber"
                                    value={formData.returnFlightNumber}
                                    onChange={(e) => handleInputChange('returnFlightNumber', e.target.value)}
                                    placeholder="e.g., AA5678"
                                    required
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Extra Information */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <h3 className="text-lg font-semibold">Extra Information</h3>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="amountOfLuggage">Amount of Luggage</Label>
                              <Input
                                id="amountOfLuggage"
                                value={formData.amountOfLuggage}
                                onChange={(e) => handleInputChange('amountOfLuggage', e.target.value)}
                                placeholder="e.g., 2 suitcases"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="remarks">Remarks</Label>
                              <Textarea
                                id="remarks"
                                value={formData.remarks}
                                onChange={(e) => handleInputChange('remarks', e.target.value)}
                                placeholder="Any additional remarks..."
                                rows={3}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Extra Items Section */}
                        {activeExtraItems && activeExtraItems.length > 0 && (
                          <div className="space-y-4 p-6 bg-gradient-to-br from-primary/5 to-accent/5 rounded-xl border-2 border-primary/20 shadow-lg hover:shadow-xl transition-shadow duration-300">
                            <div className="flex items-start gap-3 mb-4">
                              <div className="w-12 h-12 bg-gradient-to-br from-primary to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                                <Package className="h-6 w-6 text-white" />
                              </div>
                              <div>
                                <h3 className="text-xl font-bold text-foreground mb-2">Extra Items</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                  Please select the total number of extra items for your transfers. 
                                  If you arrive with more extra items than specified at booking, 
                                  we cannot guarantee to transport them. In case we are able to 
                                  transport them, we will charge you an additional fee.
                                </p>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {activeExtraItems.map((item) => (
                                <div 
                                  key={item.id} 
                                  className="flex items-start space-x-3 p-4 bg-background rounded-lg border-2 border-border hover:border-primary hover:shadow-md transition-all duration-200"
                                >
                                  <Checkbox
                                    id={`extra-${item.id}`}
                                    checked={selectedExtras.some(e => e.extra_item_id === item.id)}
                                    onCheckedChange={(checked) => handleExtraToggle(item, checked as boolean)}
                                    className="mt-1"
                                  />
                                  <div className="flex-1">
                                    <label
                                      htmlFor={`extra-${item.id}`}
                                      className="text-sm font-semibold leading-none cursor-pointer flex items-center gap-2 mb-1"
                                    >
                                      <span className="flex items-center gap-1.5 flex-1">
                                        {getIconComponent(item.icon || 'Package')}
                                        {item.name}
                                      </span>
                                      <Badge variant="secondary" className="bg-primary/10 text-primary font-bold">
                                        ${item.price.toFixed(2)}
                                      </Badge>
                                    </label>
                                    {item.description && (
                                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                                        {item.description}
                                      </p>
                                    )}
                                    {selectedExtras.some(e => e.extra_item_id === item.id) && (
                                      <div className="mt-3 flex items-center gap-2 p-2 bg-primary/5 rounded">
                                        <Label className="text-xs font-medium">Quantity:</Label>
                                        <Input
                                          type="number"
                                          min="1"
                                          max="10"
                                          value={selectedExtras.find(e => e.extra_item_id === item.id)?.quantity || 1}
                                          onChange={(e) => handleExtraQuantityChange(item.id, parseInt(e.target.value))}
                                          className="w-20 h-8 text-center font-semibold"
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Payment Methods */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <CreditCard className="h-5 w-5 text-primary" />
                            <h3 className="text-lg font-semibold">Payment Method</h3>
                          </div>
                          
                          {paymentLoading ? (
                            <div className="animate-pulse space-y-2">
                              <div className="h-12 bg-muted rounded"></div>
                              <div className="h-12 bg-muted rounded"></div>
                            </div>
                          ) : paymentMethods.length > 0 ? (
                            <RadioGroup
                              value={formData.paymentMethod}
                              onValueChange={(value) => handleInputChange('paymentMethod', value)}
                              className="space-y-3"
                            >
                              {paymentMethods.map((method) => (
                                <div key={method.id} className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-muted/50">
                                  <RadioGroupItem value={method.method_type} id={method.method_type} />
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      {method.method_type === 'cash' && <Banknote className="h-4 w-4" />}
                                      {method.method_type === 'paypal' && <DollarSign className="h-4 w-4" />}
                                      {method.method_type === 'stripe' && <CreditCard className="h-4 w-4" />}
                                      <Label htmlFor={method.method_type} className="font-medium cursor-pointer">
                                        {getMethodTitle(method.method_type)}
                                        {method.method_type === 'paypal' && (
                                          <Badge variant="secondary" className="ml-2 text-xs">
                                            Credit Cards Supported
                                          </Badge>
                                        )}
                                      </Label>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                      {method.method_type === 'paypal' 
                                        ? 'Pay securely with PayPal, credit cards, or debit cards'
                                        : getMethodDescription(method.method_type)
                                      }
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </RadioGroup>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              No payment methods are currently available
                            </div>
                          )}
                        </div>

                        <Button type="submit" className="w-full" size="lg">
                          <CreditCard className="h-5 w-5 mr-2" />
                          {formData.paymentMethod === 'paypal' ? 'Create Booking & Pay with PayPal' : 'Proceed to Payment'}
                        </Button>
                      </form>
                    </CardContent>
                  </Card>
                </div>

                {/* Booking Summary */}
                <div className="lg:col-span-1">
                  <Card className="sticky top-8 shadow-xl border-border/50">
                    <div className="bg-gradient-to-r from-primary/10 via-purple-500/10 to-primary/10 p-1">
                      <CardHeader className="bg-background">
                        <CardTitle className="text-xl">Booking Summary</CardTitle>
                      </CardHeader>
                    </div>
                    <CardContent className="space-y-4">
                      {/* Route Information */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">From:</span> {bookingData.from}
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">To:</span> {bookingData.to}
                        </div>
                      </div>

                      <Separator />

                      {/* Trip Details */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{bookingData.passengers} Passenger{bookingData.passengers > 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <Badge variant={bookingData.tripType === 'roundtrip' ? 'default' : 'secondary'}>
                            {bookingData.tripType === 'roundtrip' ? 'Round Trip' : 'One Way'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          {bookingData.serviceType === 'vip' ? (
                            <>
                              <Crown className="h-4 w-4 text-yellow-500" />
                              <Badge variant="default">VIP Service</Badge>
                            </>
                          ) : (
                            <>
                              <Car className="h-4 w-4 text-muted-foreground" />
                              <Badge variant="secondary">Standard Service</Badge>
                            </>
                          )}
                        </div>
                      </div>

                      <Separator />

                      {/* Pricing */}
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Base Price:</span>
                          <span>${(bookingData.price / (bookingData.tripType === 'roundtrip' ? 2 : 1)).toFixed(2)}</span>
                        </div>
                        {bookingData.tripType === 'roundtrip' && (
                          <div className="flex justify-between text-sm">
                            <span>Round Trip (x2):</span>
                            <span>x2</span>
                          </div>
                        )}
                        
                        {selectedExtras.length > 0 && (
                          <>
                            <Separator />
                            <div className="space-y-2">
                              <h4 className="font-medium text-sm">Extra Items</h4>
                              {selectedExtras.map((extra) => (
                                <div key={extra.extra_item_id} className="flex justify-between text-sm">
                                  <span className="text-muted-foreground">
                                    {extra.name} x{extra.quantity}
                                  </span>
                                  <span className="font-medium">
                                    ${(extra.price * extra.quantity).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                        
                        <Separator />
                        <div className="flex justify-between font-bold">
                          <span>Total:</span>
                          <span className="text-lg">${totalWithExtras.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="text-xs text-muted-foreground">
                        * Prices include all taxes and fees
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            ) : (
              // PayPal Checkout Screen
              <div className="max-w-2xl mx-auto">
                <Card>
                  <CardHeader>
                    <CardTitle>Complete Your Payment</CardTitle>
                    <p className="text-muted-foreground">
                      Booking #{currentBooking?.booking_number} - ${totalWithExtras.toFixed(2)}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="bg-muted/50 p-4 rounded-lg">
                      <h3 className="font-medium mb-2">Booking Details</h3>
                      <div className="text-sm space-y-1">
                        <p><span className="font-medium">From:</span> {bookingData.from}</p>
                        <p><span className="font-medium">To:</span> {bookingData.to}</p>
                        <p><span className="font-medium">Passengers:</span> {bookingData.passengers}</p>
                        <p><span className="font-medium">Service:</span> {bookingData.serviceType === 'vip' ? 'VIP' : 'Standard'}</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-medium mb-4">Choose Payment Method</h3>
                      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                        <p className="text-sm text-blue-800">
                          ✅ <strong>Multiple Payment Options:</strong> Use PayPal, credit cards, or debit cards - all processed securely
                        </p>
                      </div>
                      {paypalOptions && currentBooking ? (
                        <PayPalScriptProvider options={paypalOptions}>
                          <PayPalCheckout 
                            booking={currentBooking}
                            amount={totalWithExtras.toString()}
                            currency="USD"
                          />
                        </PayPalScriptProvider>
                      ) : paypalConfigError ? (
                        <div className="text-center py-4 text-red-600">
                          {paypalConfigError}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-muted-foreground">
                          Loading payment options...
                        </div>
                      )}
                    </div>

                    <div className="text-center">
                      <Button 
                        variant="outline" 
                        onClick={() => setShowPayPalCheckout(false)}
                      >
                        Back to Booking Form
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            </div>
          </div>
        </div>
      </Layout>
  );
};